import { emptyProgress } from "../game/events";
import { applyEvent } from "../game/results";
import { defaultSave, SettingsSchema } from "../save/schema";
import type { LocalChild, Store } from "../save/store";
import {
  eventPayload,
  purchaseEventIn,
  rankEventIn,
  sessionEvent,
  type ChildRow,
  type PulledEvent,
  type PurchaseRow,
  type RankRow,
  type SessionRow,
} from "./wire";

/*
  Sync in two halves. runSync talks to the server using a snapshot of the
  store and returns what it learned. mergeSync applies that to whatever the
  store holds by then, so play that happened during the sync is kept. Both
  halves are tested against a fake server.
*/

/** "network": try again later. "rejected": the server refused this item for good. "auth": sign in again. */
export class CloudError extends Error {
  constructor(
    readonly kind: "network" | "rejected" | "auth",
    message: string,
  ) {
    super(message);
  }
}

export interface CloudApi {
  upsertChild(c: LocalChild): Promise<ChildRow>;
  removeChild(id: string): Promise<void>;
  record(
    fn: "record_session" | "record_rank_change" | "record_purchase",
    body: object,
  ): Promise<number>;
  listChildren(): Promise<ChildRow[]>;
  sessionsAfter(seq: number, limit: number): Promise<SessionRow[]>;
  rankChangesAfter(seq: number, limit: number): Promise<RankRow[]>;
  purchasesAfter(seq: number, limit: number): Promise<PurchaseRow[]>;
}

export const PAGE = 200;

export interface SyncResult {
  /** Children sent, with the updatedAt that was sent. */
  sent: { id: string; updatedAt: string }[];
  /** Events the server refused for good, with why. */
  rejected: { childId: string; eventId: string; reason: string }[];
  children: ChildRow[] | null;
  pulled: PulledEvent[];
  cursor: number;
  error: CloudError | null;
  at: string;
}

async function pullEvents(api: CloudApi, from: number) {
  const pulled: PulledEvent[] = [];
  let cursor = from;
  for (;;) {
    const [sessions, ranks, purchases] = await Promise.all([
      api.sessionsAfter(cursor, PAGE),
      api.rankChangesAfter(cursor, PAGE),
      api.purchasesAfter(cursor, PAGE),
    ]);
    // A full page may have more after it, so only trust events up to the
    // smallest last seq of any full page.
    const fullEnds = [sessions, ranks, purchases]
      .filter((rows) => rows.length >= PAGE)
      .map((rows) => rows[rows.length - 1]!.seq);
    const limit = fullEnds.length ? Math.min(...fullEnds) : Infinity;
    const batch = [
      ...sessions.map(sessionEvent),
      ...ranks.map(rankEventIn),
      ...purchases.map(purchaseEventIn),
    ]
      .filter((p): p is PulledEvent => p !== null && p.seq <= limit)
      .sort((a, b) => a.seq - b.seq);
    const allSeqs = [...sessions, ...ranks, ...purchases]
      .map((r) => r.seq)
      .filter((s) => s <= limit);
    pulled.push(...batch);
    if (allSeqs.length) cursor = Math.max(cursor, ...allSeqs);
    if (!fullEnds.length) return { pulled, cursor };
  }
}

export async function runSync(
  snapshot: Store,
  api: CloudApi,
  now = new Date(),
): Promise<SyncResult> {
  const result: SyncResult = {
    sent: [],
    rejected: [],
    children: null,
    pulled: [],
    cursor: snapshot.cursor,
    error: null,
    at: now.toISOString(),
  };
  const fail = (e: unknown) => {
    result.error = e instanceof CloudError ? e : new CloudError("network", String(e));
    return result;
  };

  try {
    // 1. Children first, so events have somewhere to go.
    // Only children that belong to the family; ones from before signing in
    // wait until a parent chooses where their progress goes.
    for (const c of snapshot.children) {
      if (!c.dirty || !c.synced) continue;
      if (c.removed) {
        if (c.synced) await api.removeChild(c.id);
      } else {
        await api.upsertChild(c);
      }
      result.sent.push({ id: c.id, updatedAt: c.updatedAt });
    }

    // 2. Then each child's events, in order.
    for (const c of snapshot.children) {
      if (c.removed || !c.synced) continue;
      for (const e of c.outbox) {
        const { fn, body } = eventPayload(c.id, snapshot.deviceId, e);
        try {
          await api.record(fn, body);
        } catch (err) {
          if (err instanceof CloudError && err.kind === "rejected")
            result.rejected.push({ childId: c.id, eventId: e.id, reason: err.message });
          else throw err;
        }
      }
    }

    // 3. Then everything new from every device.
    result.children = await api.listChildren();
    const { pulled, cursor } = await pullEvents(api, snapshot.cursor);
    result.pulled = pulled;
    result.cursor = cursor;
  } catch (e) {
    return fail(e);
  }
  return result;
}

function childFromRow(row: ChildRow): LocalChild {
  return {
    id: row.id,
    name: row.name,
    pin: row.pin,
    settings: SettingsSchema.catch(defaultSave().settings).parse(row.settings),
    updatedAt: row.updated_at,
    dirty: false,
    mode: "add",
    onboarded: true,
    celebrate: [],
    synced: true,
    removed: !!row.deleted_at,
    base: emptyProgress(),
    outbox: [],
  };
}

/** Apply a sync's result to the store as it is now. Pure. */
export function mergeSync(latest: Store, r: SyncResult): Store {
  let children = latest.children.map((c) => {
    const sent = r.sent.find((s) => s.id === c.id);
    // Only clear `dirty` if nothing changed since it was sent.
    return sent && sent.updatedAt === c.updatedAt ? { ...c, dirty: false } : c;
  });

  if (r.children) {
    for (const row of r.children) {
      const i = children.findIndex((c) => c.id === row.id);
      if (i < 0) {
        if (!row.deleted_at) children.push(childFromRow(row));
        continue;
      }
      const c = children[i]!;
      const theirs = childFromRow(row);
      children[i] = {
        ...c,
        synced: true,
        removed: c.removed || theirs.removed,
        ...(!c.dirty && row.updated_at > c.updatedAt
          ? {
              name: theirs.name,
              pin: theirs.pin,
              settings: theirs.settings,
              updatedAt: theirs.updatedAt,
            }
          : {}),
      };
    }
  }

  const drop = new Set(r.rejected.map((x) => x.eventId));
  for (const p of r.pulled) drop.add(p.event.id);
  children = children.map((c) => {
    let base = c.base;
    for (const p of r.pulled) if (p.childId === c.id) base = applyEvent(base, p.event);
    return {
      ...c,
      base,
      outbox: c.outbox.filter((e) => !drop.has(e.id)),
    };
  });

  return {
    ...latest,
    children,
    cursor: Math.max(latest.cursor, r.cursor),
    lastSync: r.error ? latest.lastSync : r.at,
  };
}
